import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link as MuiLink
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
//import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';

const OTPVerificationPage = () => {
  const navigate = useNavigate();
  //const { t } = useTranslation();
  const { verifyOTP, resendOTP, loading, error, tempEmail, otpRequired, logout } = useAuth();
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [localError, setLocalError] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [attemptsExceeded, setAttemptsExceeded] = useState(false);

  // Redirect to login if OTP is not required
  useEffect(() => {
    if (!otpRequired) {
      navigate('/auth');
    }
  }, [otpRequired, navigate]);

  // Timer for resend OTP button
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Check for max attempts exceeded error
  useEffect(() => {
    if (errorCode === 'MAX_ATTEMPTS_EXCEEDED' || (localError && localError.includes('Maximum OTP attempts exceeded'))) {
      setAttemptsExceeded(true);
      // Auto logout and redirect after 3 seconds
      const timer = setTimeout(() => {
        logout();
        navigate('/auth');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorCode, localError, logout, navigate]);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
      setLocalError('');
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLocalError('');
    setErrorCode(null);
    setSuccessMessage('');

    if (!otp || otp.length !== 6) {
      setLocalError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      await verifyOTP(otp);
      setSuccessMessage('OTP verified successfully! Redirecting...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      const errorMessage = err.message || 'OTP verification failed';
      setLocalError(errorMessage);
      // Check for max attempts error code from API response
      if (err.response?.data?.code === 'MAX_ATTEMPTS_EXCEEDED') {
        setErrorCode('MAX_ATTEMPTS_EXCEEDED');
      }
    }
  };

  const handleResendOTP = async () => {
    setLocalError('');
    setSuccessMessage('');
    setOtp('');

    try {
      await resendOTP();
      setSuccessMessage('OTP resent successfully to your email');
      setResendTimer(60); // 60 seconds before can resend again
    } catch (err) {
      setLocalError(err.message);
    }
  };

  const handleBackToLogin = () => {
    logout();
    navigate('/auth');
  };

  const displayError = localError || error;

  // Render max attempts exceeded screen
  if (attemptsExceeded) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <img
                src="/logo.png"
                alt="KKCW Logo"
                style={{
                  height: 120,
                  width: 120,
                  borderRadius: 8,
                  marginBottom: 16
                }}
              />
              <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
                Too Many Failed Attempts
              </Typography>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              You have exceeded the maximum number of OTP verification attempts. For security reasons, please login again to request a new OTP.
            </Alert>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Redirecting to login page in 3 seconds...
              </Typography>
              <CircularProgress size={40} />
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <MuiLink
                component="button"
                type="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  logout();
                  navigate('/auth');
                }}
                sx={{ cursor: 'pointer' }}
              >
                Click here to return to login immediately
              </MuiLink>
            </Box>

            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
              Kasikannu Catering World • 2FA Verification
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <img
              src="/logo.png"
              alt="KKCW Logo"
              style={{
                height: 120,
                width: 120,
                borderRadius: 8,
                marginBottom: 16
              }}
            />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              Verify Your Account
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
              Enter the 6-digit OTP sent to
              <br />
              <strong>{tempEmail}</strong>
            </Typography>
          </Box>

          {displayError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {displayError}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleVerifyOTP} sx={{ mt: 3 }}>
            <TextField
              label="Enter OTP"
              value={otp}
              onChange={handleOtpChange}
              fullWidth
              margin="normal"
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: { fontSize: '32px', letterSpacing: '10px', textAlign: 'center' }
              }}
              required
              autoFocus
            />

            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              OTP is valid for 5 minutes
            </Typography>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || otp.length !== 6}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify OTP'}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              onClick={handleResendOTP}
              disabled={loading || resendTimer > 0}
              sx={{ mb: 2 }}
            >
              {resendTimer > 0
                ? `Resend OTP (${resendTimer}s)`
                : 'Resend OTP'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <MuiLink
                component="button"
                type="button"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  handleBackToLogin();
                }}
                sx={{ cursor: 'pointer' }}
              >
                Back to Login
              </MuiLink>
            </Box>
          </Box>

          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
            Kasikannu Catering World • 2FA Verification
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default OTPVerificationPage;
